// ═══════════════════════════════════════════════════════════════════
// Plaza3D.jsx — THE PLAZA, IN THREE DIMENSIONS (Plaza 3D, Ship 1)
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "can we leave the plaza exactly how it is … but
// flip a 3D switch and have the plaza be 3D with all 3D models you
// make — even V'ael, Willie, everyone, the fountain, the portals!"
//
// ⭐ THIS IS A RENDERER, NOT A GAME. (Shatterfield3D's own law, again.)
//
// It takes the SAME zone table (plazaZones.js), the SAME position refs
// and the SAME presence map the painted Plaza uses, and paints them in
// three dimensions. Login, the movement loop, collision, the prompts,
// the RoomConsole, the menus, the kiosks, presence, chat, challenges —
// Plaza.js owns all of it and none of it knows this file exists. The
// switch (plazaCamera.getPlaza3D) swaps ONE <div> in Plaza.js's render
// — the map block — for this component. Off, the Plaza is byte-for-
// byte the room that shipped.
//
// ── THE CAMERA CONTRACT (ie-3d-conversion), all four pieces ─────────
//   1 · two views, shared and PERSISTED — Follow (0.5×) and Trail (1×);
//       a view is a distance MULTIPLIER, never a position.
//   2 · FREE POSE — drag and the rig stops steering and TRANSLATES the
//       pose you chose; it never re-aims.
//   3 · DEFAULT is an ACTION driven by a COUNTER.
//   4 · MOVEMENT FOLLOWS THE EYE — the rig publishes its ground-forward
//       to plazaCamera.js and Plaza.js's ONE movement loop rotates its
//       key vector by it (rotateGridInput). W is "away from the camera".
//
// ── PERF LAW (ie-realtime-perf) ─────────────────────────────────────
//   · React.memo on the host. Plaza.js re-renders on every presence
//     tick and every step; every live value reaches the scene through a
//     REF read in useFrame, so the bail is safe by construction.
//   · Where everyone IS lives in `othersRef`; the ROSTER (who) is the
//     only thing that reconciles, and only when somebody arrives/leaves.
//   · Name plates are painted canvases, not <Html>. Chat floaties are
//     <Html> because they are DOM text that lives three seconds.
//   · The keys ref is never replaced, only read.
//
// ⚠ drei's <Html transform> renders NOTHING on caP'n's machine (two
// sessions lost). Only plain billboard <Html> is used here. Do not
// "upgrade" it.
// ⚠ React context does NOT cross the <Canvas> boundary. Everything
// shared with the outside goes through plazaCamera (a module), never a
// Provider.
// ⚠ terror-assets sends no CORS headers: a companion's webp can never be
// a texture. Companions with a kit body get the body; the rest ride a
// billboard <Html> <img>, same as the 2D follower.
// ═══════════════════════════════════════════════════════════════════
import { GraceAura3D } from "./GraceAura"; // KING'S GRACE
import { GRACES, renderableGrace } from "./kingsGrace";
import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { GM, OBSIDIAN_SLAB, facetTB, TEXT_SET, textGlow } from "./theme";
import { getCompanionSprite } from "./engineIndex";
import { getEquippedTitle } from "./CosmeticOverlay";
import { getBeingPalette, canvasTex } from "./ieBeingModels";
import { measureSolid } from "./fieldBeings3D";
import { companionBodyOf } from "./companionBody3D";
import { BeingBody } from "./sculptedBeings"; // TERROR TOONZ: the sculpted body where one exists (2026-09-23)
import {
  VIEWS, VIEW_ORDER, getView, chooseView, viewMult, isPosed, setPosed,
  resetCamera, resetToken, subscribe, setGroundForward, resetGroundForward,
  getFocus, focusToken, setFocus, clearFocus,
} from "./plazaCamera";
import {
  LIGHT, G,
  UnstableWorld, rectBounds, SHELF_ROWS, WALL_N,
} from "./plazaPieces3D";
import { FolkBody, folkHeight, folkIdFor } from "./plazaFolk3D";
import { FrameWatch } from "./sculptGate"; // THE FAIL-SAFE LAW — the Plaza watches its own frame rate
import { SculptedRoom } from "./plazaShell3D";
import { SculptedPortals } from "./portal3D";
import { SculptedBulletin, SculptedBench, SculptedStall, SculptedKiosk, SculptedTent } from "./plazaFixtures3D";   // the fixtures, built in Blender (2026-09-22)      // the five doors, stone built in Blender (2026-09-22)   // the Plaza's stone, built in Blender (caP'n, 2026-09-22)
import { SculptedFountain } from "./fountain3D"; // the sculpted fountain (caP'n, 2026-09-16, THE PLAZA UPDATE #4)

// ── DIALS ──────────────────────────────────────────────────────────
const COMPANION_H = 0.95;     // a kit companion's height, world units
const LERP_K = 0.001;         // per-second remainder of the chase (Pier's number)

// ═══════════════════════════════════════════════════════════════════
// ⭐⭐ THE FIXTURE ROWS ⭐⭐ — zone id → what stands there.
// Plaza.js's zone table is the placement; this map is the look. A new
// row in plazaZones with no row here renders nothing and collides as
// the table says — never the other way round.
// ═══════════════════════════════════════════════════════════════════
const FIXTURES = {
  bulletin:   (z) => <SculptedBulletin rect={z.solid} />,   // Blender-built; <BulletinBoard/> is its fail-safe
  herald:     (z) => <SculptedBench rect={z.solid} />,      // Blender-built; <WillyBench/> is its fail-safe
  vyren:      (z) => <SculptedStall rects={z.solid} />,     // Blender-built; <SweetTreatsStall/> is its fail-safe
  fountain:   (z) => <SculptedFountain rect={z.solid} />,   // the primitive <Fountain/> is its fail-safe
  pack_shop:  (z) => <SculptedKiosk rect={z.art || z.solid} />,   // drawn from `art` (where it stands), not `solid` (where you can't walk)     // Blender-built; <PackKiosk/> is its fail-safe
  deck_forge: (z) => <SculptedTent rects={z.solid} />,      // Blender-built; <WorkshopTent/> is its fail-safe
};

// Where the people stand, relative to their fixture's box. Facing is
// the Plaza's own {x,y} (y=+1 is toward the camera).
function npcPlacements(zones) {
  const out = [];
  const by = Object.fromEntries(zones.map((z) => [z.id, z]));
  const herald = rectBounds(by.herald?.solid);
  if (herald) out.push({ id: "willy", spec: "willy", x: herald.cx + herald.w * 0.12, z: herald.cz - 0.95, facing: { x: 0, y: 1 } });
  const vy = rectBounds(by.vyren?.solid);
  // 0.9, not 1.1: her sculpted coil lies round her on the ground, so she stands
  // a little further back and the coil stays behind the counter (2026-09-16)
  // ⚑ facing STRAIGHT south (2026-09-23): her rebuilt coil is sculpted round the booth itself,
  // in its frame — any turn swings the arches off the posts. She turns her HEAD to you instead.
  if (vy) out.push({ id: "vyen", spec: "vyen", x: vy.x1 + 1 - 2.6, z: vy.y0 + 0.9, facing: { x: 0, y: 1 } });
  const pk = rectBounds(by.pack_shop?.art || by.pack_shop?.solid);
  // turned kiosk (taller than wide) backs onto the west wall — she faces east, out at the player (2026-09-22)
  if (pk) out.push(pk.d > pk.w
    ? { id: "venus", spec: "venus", x: pk.cx + 0.1, z: pk.cz + 0.2, facing: { x: 1, y: 0.1 } }
    : { id: "venus", spec: "venus", x: pk.cx - 0.2, z: pk.cz + 0.1, facing: { x: 0.1, y: 1 } });
  // ⭐ V'AEL — the first time she has had a body in her own Plaza. She
  // stands on the south-east step of her fountain, watching the spawn.
  // No zone, no prompt, no line: the fountain's "Toss a Coin" is still
  // the fountain's. Giving her a VERB is a design call (PRIME §2.x:
  // "there is no V'ael in the Plaza … giving her a body is a design
  // session") — this is the body; the verb is caP'n's to add.
  const fo = rectBounds(by.fountain?.solid);
  if (fo) out.push({
    id: "vael", spec: "vael", x: fo.cx + fo.w * 0.36, z: fo.cz + fo.d * 0.42, y: 0.2,
    facing: { x: -0.55, y: 1 },
    // SHE WALKS NOW (caP'n, 2026-09-22). x/z/y above are kept: delete `walk`
    // and she goes straight back to standing on the rim. See NpcWander.
    walk: {
      // one tile further out from the basin (caP'n, 2026-09-22: "have V'ael walk one
      // tile away from the fountain") — row 14 is clear across since the forge shrank
      x0: fo.cx - fo.w * 0.36, x1: fo.cx + fo.w * 0.36, z: fo.y1 + 2.6,
      mps: 0.36, pause: [4.0, 9.0],
    },
  });
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// NAME PLATES — a picture of some words (ie-realtime-perf 5a).
// ═══════════════════════════════════════════════════════════════════
const plateTex = (name, title, color) => canvasTex(`plaza3d:plate:${name}|${title || ""}|${color}`, 256, 96, (g, w, h) => {
  g.clearRect(0, 0, w, h);
  const r = 18, x = 8, y = title ? 6 : 22, bw = w - 16, bh = title ? 84 : 52;
  g.fillStyle = "rgba(4,2,14,0.86)"; g.beginPath(); g.roundRect(x, y, bw, bh, r); g.fill();
  g.strokeStyle = color + "88"; g.lineWidth = 2; g.stroke();
  g.textAlign = "center"; g.textBaseline = "middle"; g.shadowColor = "#000"; g.shadowBlur = 4;
  g.fillStyle = color; g.font = "bold 30px Georgia, serif"; g.fillText(name, w / 2, y + (title ? 26 : 26));
  if (title) { g.fillStyle = "#d8cfe8"; g.font = "italic 20px Georgia, serif"; g.fillText(title, w / 2, y + 60); }
});
function NamePlate({ name, title, color = "#f0cc60", y }) {
  const ref = useRef();
  const tex = useMemo(() => plateTex(name, title, color), [name, title, color]);
  useFrame(({ camera }) => { const o = ref.current; if (!o) return; o.rotation.y = Math.atan2(camera.position.x - o.getWorldPosition(_wp).x, camera.position.z - _wp.z); });
  return (
    <mesh ref={ref} position={[0, y, 0]}>
      <planeGeometry args={[1.5, 0.56]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} toneMapped={false} />
    </mesh>
  );
}
const _wp = new THREE.Vector3();

// ═══════════════════════════════════════════════════════════════════
// THE COMPANION — the kit body a tile behind you, or its webp if it
// has no body (the four Esclings, the trophies, J'ynxi).
// ═══════════════════════════════════════════════════════════════════
const RIG_SLOTS = ["head", "armL", "armR", "legL", "legR", "tail"];
function KitBody({ info, ctl }) {
  const outer = useRef(); const inner = useRef(); const fitted = useRef(false); const rig = useRef(null);
  // TERROR TOONZ (2026-09-23): the companion wears its SCULPT when it has one (BeingBody, the painted
  // kit body behind it as the fail-safe) — before this the 3D Plaza always drew the painted body.
  // The sculpt walks on its own legs (sculptCtl.speed); we own the yaw, so its own facing stays forward.
  const sculptCtl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const skin = useRef("?");
  const onSkin = React.useCallback((k) => { skin.current = k; fitted.current = false; }, []);
  const yaw = useRef(0); const ph = useRef(0);
  const pal = useMemo(() => getBeingPalette(info.lineage), [info.lineage]);
  useFrame(({ clock }, dtRaw) => {
    const o = outer.current, g = inner.current; if (!o || !g) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime();
    if (!fitted.current) {
      g.scale.setScalar(1); g.position.set(0, 0, 0); g.updateMatrixWorld(true);   // a re-fit (the body swapped) starts clean
      const mm = measureSolid(g);
      if (mm && isFinite(mm.h) && mm.h > 1e-3) {
        const s = COMPANION_H / mm.h; g.scale.setScalar(s); g.position.set(-mm.cx * s, -mm.minY * s, -mm.cz * s);
        const R = {}; RIG_SLOTS.forEach((k) => { const n = g.getObjectByName("rig_" + k); if (n) R[k] = { o: n, bx: n.rotation.x, bz: n.rotation.z }; });
        rig.current = R; fitted.current = true;
      }
    }
    const f = ctl.facing || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    const speed = ctl.speed || 0; ph.current += speed * dt * 9;
    const sculpted = skin.current === "s";
    sculptCtl.speed = speed;
    o.rotation.set(0, yaw.current, 0);
    o.position.y = speed ? Math.abs(Math.sin(ph.current)) * 0.05 * (sculpted ? 0.3 : 1) : 0;
    const R = rig.current; if (!R) return;
    // a sculpt walks on its own legs: don't swing them a second time
    const legs = speed && !sculpted ? Math.sin(ph.current) * 0.5 : 0, arms = speed && !sculpted ? -Math.sin(ph.current) * 0.3 : 0;
    const set = (k, x, z) => { const r = R[k]; if (r) { r.o.rotation.x = r.bx + x; r.o.rotation.z = r.bz + z; } };
    set("legL", legs, 0); set("legR", -legs, 0); set("armL", arms, 0); set("armR", -arms, 0);
    if (R.head) R.head.o.rotation.x = R.head.bx + Math.sin(t * 0.7) * 0.06;
    if (R.tail) R.tail.o.rotation.z = R.tail.bz + Math.sin(t * 3.1) * 0.25;
  });
  return (
    <group ref={outer}>
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.36, 16]} /><meshBasicMaterial color={pal?.glow || "#c8963c"} transparent opacity={0.14} depthWrite={false} toneMapped={false} /></mesh>
      <group ref={inner}><BeingBody lineage={info.lineage} tier={info.tier} ctl={sculptCtl} onSkin={onSkin} /></group>
    </group>
  );
}
function Companion({ read, companionId, radiantTier }) {
  const grace = renderableGrace(radiantTier);
  const g = useRef(); const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const info = useMemo(() => companionBodyOf(companionId), [companionId]);
  const last = useRef(null);
  useFrame((_, dtRaw) => {
    const o = g.current; if (!o) return; const p = read(); if (!p) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const k = 1 - Math.pow(LERP_K * 8, dt);   // a little lazier than the player — it trails
    const tx = p.x + 0.5, tz = p.y + 0.5;
    if (!last.current) { o.position.set(tx, 0, tz); last.current = { x: tx, z: tz }; }
    o.position.x += (tx - o.position.x) * k; o.position.z += (tz - o.position.z) * k;
    const mv = Math.hypot(o.position.x - last.current.x, o.position.z - last.current.z);
    ctl.speed = mv > 0.0025 ? 1 : 0;
    if (mv > 0.0025) ctl.facing = { x: o.position.x - last.current.x, y: o.position.z - last.current.z };
    else if (p.fx !== undefined) ctl.facing = { x: p.fx, y: p.fy };
    last.current = { x: o.position.x, z: o.position.z };
  });
  const glow = grace ? GRACES[grace].color : null;
  return (
    <group ref={g}>
      {info ? <KitBody info={info} ctl={ctl} /> : (
        <Html center position={[0, 0.55, 0]} style={{ pointerEvents: "none" }} zIndexRange={[5, 0]}>
          <img src={getCompanionSprite(companionId)} alt="" style={{ width: 54, height: 54, objectFit: "contain", imageRendering: "pixelated", filter: glow ? `drop-shadow(0 0 6px ${glow})` : "drop-shadow(0 4px 6px rgba(0,0,0,.7))" }} />
        </Html>
      )}
      <RidingHit fkey={"pet:" + companionId + ":" + (radiantTier ?? "r")} label="companion" h={info ? COMPANION_H : 1.0} w={0.9} size={1.6} />
      {/* KING'S GRACE — the worn foil as light, at the companion's feet */}
      {grace && <GraceAura3D graceId={grace} height={info ? COMPANION_H : 1.0} />}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AN ACTOR — a player, local or remote. Position comes out of `read()`
// inside useFrame, never out of a prop. The body owns its pose; this
// owns where it stands and how fast it got there.
// ═══════════════════════════════════════════════════════════════════
function Actor({ read, gender, name, title, isLocal, color }) {
  const g = useRef(); const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const id = folkIdFor(gender);
  const last = useRef(null); const still = useRef(0);
  useFrame((_, dtRaw) => {
    const o = g.current; if (!o) return; const p = read(); if (!p) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const tx = p.x + 0.5, tz = p.y + 0.5;
    if (!last.current) { o.position.set(tx, 0, tz); last.current = { x: tx, z: tz }; }
    if (isLocal) { o.position.x = tx; o.position.z = tz; }   // the Plaza already smooths visualPosRef
    else { const k = 1 - Math.pow(LERP_K, dt); o.position.x += (tx - o.position.x) * k; o.position.z += (tz - o.position.z) * k; }
    const mv = Math.hypot(o.position.x - last.current.x, o.position.z - last.current.z);
    // a step is ~130ms of motion then a hold; keep "walking" alive across the hold
    if (mv > 0.002) still.current = 0.12; else still.current -= dt;
    ctl.speed = still.current > 0 ? 1 : 0;
    ctl.facing = { x: p.fx || 0, y: p.fy === undefined ? 1 : p.fy };
    last.current = { x: o.position.x, z: o.position.z };
  });
  return (
    <group ref={g}>
      <FolkBody id={id} ctl={ctl} />
      <NamePlate name={name || "—"} title={title} color={color} y={folkHeight(id) + 0.42} />
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE LOCK-ON — click a being, a pet or a fixture and the lens locks
// onto it (caP'n, 2026-09-23). Every clickable is an INVISIBLE box, not
// the model itself: raycasting a skinned .glb is slow and misses on the
// pose, and a box is the same size the eye expects anyway. It is drawn
// (opacity 0, no depth, no colour) because three skips `visible=false`
// only in the renderer — r3f's raycast still needs a real mesh.
// `get(out)` fills a Vector3 with the lock point, every frame.
// ═══════════════════════════════════════════════════════════════════
const HIT_MAT = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, colorWrite: false });
const HIT_BOX = new THREE.BoxGeometry(1, 1, 1);
// a drag that happens to start and end on something is an orbit, not a click
function lockOn(e, f) { e.stopPropagation(); if ((e.delta || 0) > 4) return; setFocus(f); }
const DOWN = { x: 0, y: 0 };
function hover(on) { if (typeof document !== "undefined") document.body.style.cursor = on ? "pointer" : ""; }
// a hit box at a fixed spot (fixtures, portals, standing folk)
function FixedHit({ fkey, label, pos, dims, size }) {
  const f = useMemo(() => ({ key: fkey, label, size, get: (o) => o.set(pos[0], pos[1], pos[2]) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fkey, label, size, pos[0], pos[1], pos[2]]);
  return (
    <mesh geometry={HIT_BOX} material={HIT_MAT} position={pos} scale={dims} renderOrder={-1}
      onClick={(e) => lockOn(e, f)} onPointerOver={(e) => { e.stopPropagation(); hover(true); }} onPointerOut={() => hover(false)} />
  );
}
// a hit box that rides a moving group (a walking V'ael, a pet): drop it
// INSIDE the group; the lock reads the group's world position each frame
function RidingHit({ fkey, label, h, w = 0.8, size }) {
  const m = useRef();
  const f = useMemo(() => ({ key: fkey, label, size: size || h * 1.2,
    get: (o) => { if (m.current) { m.current.getWorldPosition(o); } return o; } }), [fkey, label, h, size]);
  return (
    <mesh ref={m} geometry={HIT_BOX} material={HIT_MAT} position={[0, h / 2, 0]} scale={[w, h, w]} renderOrder={-1}
      onClick={(e) => lockOn(e, f)} onPointerOver={(e) => { e.stopPropagation(); hover(true); }} onPointerOut={() => hover(false)} />
  );
}
// how tall each fixture reads, for its lock point and its hit box (m)
const FIXTURE_H = { bulletin: 2.4, herald: 1.6, vyren: 2.8, fountain: 2.6, pack_shop: 2.8, deck_forge: 3.4 };
function FixtureHits({ zones }) {
  return zones.map((z) => {
    if (z.kind === "portal") {
      const b = rectBounds(z.zone); if (!b) return null;
      const W = Math.min(b.w * 0.72, 4.4), H = W * 1.15;
      const y = WALL_N + H / 2;
      return <FixedHit key={z.id} fkey={"z:" + z.id} label={z.label} pos={[b.cx, y, SHELF_ROWS - 2.4]} dims={[W, H, 1.2]} size={H * 1.1} />;
    }
    // her booth and his bench ARE them: the being's own box locks those (NpcStand)
    if (!FIXTURES[z.id] || z.id === "vyren" || z.id === "herald") return null;
    const b = rectBounds(z.art || z.solid); if (!b) return null;
    const H = FIXTURE_H[z.id] || 2.4;
    return <FixedHit key={z.id} fkey={"z:" + z.id} label={z.label} pos={[b.cx, H / 2, b.cz]} dims={[b.w, H, b.d]} size={Math.max(H, Math.max(b.w, b.d) * 0.7)} />;
  });
}

// ═══════════════════════════════════════════════════════════════════
// THE CAMERA RIG — the contract, all four pieces (from Pier3D, proven)
// ═══════════════════════════════════════════════════════════════════
const TMP_A = new THREE.Vector3(); const TMP_B = new THREE.Vector3(); const TMP_F = new THREE.Vector3();
function CameraRig({ posRef }) {
  const { camera, gl, size } = useThree();
  const controls = useRef();
  const lastTarget = useRef(new THREE.Vector3(posRef.current.x + 0.5, 1, posRef.current.y + 0.5));
  // ⚠ -1, not resetToken(): the FIRST frame is treated as a DEFAULT press,
  // so the lens snaps straight to its framing instead of lerping in from
  // wherever the Canvas prop or a previous mount left it. A black first
  // second is the thing this prevents.
  const lastReset = useRef(-1);
  const lastFocus = useRef(focusToken());   // the lock-on's own counter
  const glide = useRef(0);                  // seconds left of the glide onto a new lock
  // BASE framing — this room's own idea of right. A view MULTIPLIES it.
  // Portrait phones stand the lens further back (capped) so the hall
  // still reads as a place and not a wall and a lamppost.
  const fit = size.width < size.height ? Math.min(1.5, 0.8 * (size.height / size.width)) : 1;
  const BASE_DIST = 6.8 * fit, BASE_HEIGHT = 4.6 * fit;
  useEffect(() => () => resetGroundForward(), []);
  useFrame((_, dtRaw) => {
    const c = controls.current; if (!c) return;
    // dev: `window.__plazaCamFree = true` lets the review loop park the lens
    // anywhere (`__plazaCam.position.set(...); __plazaCam.lookAt(...)`)
    // without the rig snapping it back to the walker. Never set by the game.
    if (typeof window !== "undefined" && window.__plazaCamFree) { if (window.__plazaLook) c.target.fromArray(window.__plazaLook); return; }
    const p = posRef.current;
    const tx = p.x + 0.5, ty = 1.0, tz = p.y + 0.5;
    // 4 · publish the ground-forward, every frame (world z IS tile y)
    camera.getWorldDirection(TMP_A); TMP_A.y = 0;
    if (TMP_A.lengthSq() > 1e-6) { TMP_A.normalize(); setGroundForward(TMP_A.x, TMP_A.z); }
    // 3 · DEFAULT is an action, driven by a counter
    const tok = resetToken();
    if (tok !== lastReset.current) {
      lastReset.current = tok; const m = viewMult();
      camera.position.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
      c.target.set(tx, ty, tz); lastTarget.current.set(tx, ty, tz); c.update(); return;
    }
    // ⭐ THE LOCK-ON — the lens orbits the thing you clicked, not you.
    // A new lock GLIDES in (keeping the current bearing) for ~0.9 s; after
    // that, and the instant you drag, it carries whatever pose you chose
    // along with the target — the free-pose law, aimed at them.
    const fo = getFocus();
    c.minDistance = fo ? 0.7 : 2.2;
    if (fo) {
      fo.get(TMP_F);
      const ft = focusToken();
      if (ft !== lastFocus.current) { lastFocus.current = ft; glide.current = 0.9; }
      if (glide.current > 0 && !isPosed()) {
        glide.current -= Math.min(dtRaw || 0.016, 0.05);
        const sz = fo.size || 2;
        TMP_B.subVectors(camera.position, c.target); TMP_B.y = 0;
        if (TMP_B.lengthSq() < 1e-6) TMP_B.set(0, 0, 1);
        TMP_B.normalize().multiplyScalar(sz * 1.9);
        TMP_B.y = sz * 0.55;
        TMP_B.add(TMP_F);
        camera.position.lerp(TMP_B, 0.1);
        c.target.lerp(TMP_F, 0.16);
      } else {
        glide.current = 0;
        TMP_B.subVectors(TMP_F, lastTarget.current);
        camera.position.add(TMP_B); c.target.copy(TMP_F);
      }
      lastTarget.current.copy(c.target); c.update(); return;
    }
    if (isPosed()) {
      // 2 · FREE POSE — translate, never re-aim
      TMP_B.set(tx - lastTarget.current.x, ty - lastTarget.current.y, tz - lastTarget.current.z);
      camera.position.add(TMP_B); c.target.set(tx, ty, tz); lastTarget.current.set(tx, ty, tz); c.update(); return;
    }
    // 1 · the steered framing — goal-based, lerped, interruptible
    const m = viewMult();
    TMP_B.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
    camera.position.lerp(TMP_B, 0.055);
    c.target.lerp(TMP_A.set(tx, ty, tz), 0.12);
    lastTarget.current.copy(c.target); c.update();
  });
  return (
    <OrbitControls ref={controls} makeDefault enablePan={false}
      onStart={() => setPosed(true)}
      minDistance={2.2} maxDistance={30}
      maxPolarAngle={Math.PI / 1.9}
      enableDamping dampingFactor={0.08} domElement={gl.domElement} />
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE SCENE
// ═══════════════════════════════════════════════════════════════════
function Scene({ world, posRef, facingRef, gender, companionId, playerName, title, radiantTier, compOffsetRef, roster, othersRef, floaties }) {
  const { cols, rows, zones } = world;
  const portals = useMemo(() => zones.filter((z) => z.kind === "portal"), [zones]);
  const npcs = useMemo(() => npcPlacements(zones), [zones]);
  const cx = cols / 2, cz = (SHELF_ROWS + rows) / 2, R = Math.max(cols, rows) / 2;

  // the local player's reader — Plaza's own smoothed sub-tile position
  const readMe = useMemo(() => () => { const p = posRef.current, f = facingRef.current; return { x: p.x, y: p.y, fx: f?.x, fy: f?.y }; }, [posRef, facingRef]);
  // the companion trails to the tile you just left: pos + compOffset
  const readComp = useMemo(() => () => { const p = posRef.current, co = compOffsetRef.current || { x: 0, y: -1 }, f = facingRef.current; return { x: p.x + co.x, y: p.y + co.y, fx: f?.x, fy: f?.y }; }, [posRef, compOffsetRef, facingRef]);

  return (
    <>
      {/* ── LIGHT — the one-knob model. Three point lights live in the
          fixtures (fountain, forge, lantern); nothing else adds one. */}
      <ambientLight intensity={G(LIGHT.ambient)} color="#b8a8e0" />
      <hemisphereLight args={["#6a4aa0", "#1a0f2a", G(LIGHT.hemi)]} />
      <directionalLight position={[cx - 8, 14, cz + 10]} intensity={G(LIGHT.key)} color="#d8c8ff" castShadow
        shadow-mapSize-width={1024} shadow-mapSize-height={1024}
        shadow-camera-left={-cols * 0.6} shadow-camera-right={cols * 0.6} shadow-camera-top={rows * 0.7} shadow-camera-bottom={-rows * 0.7}
        shadow-camera-near={1} shadow-camera-far={50} shadow-bias={-0.0006} shadow-normalBias={0.02} />
      <fog attach="fog" args={["#0c0716", R * 1.6, R * 4.2]} />

      <UnstableWorld cx={cx} cz={cz} R={R} />
      <SculptedRoom cols={cols} rows={rows} />   {/* the Blender-built stone; <Room/> is its fail-safe */}
      <SculptedPortals portals={portals} />   {/* Blender-built arches; the primitive <Portals/> is the fail-safe */}
      {zones.map((z) => FIXTURES[z.id] ? <group key={z.id}>{FIXTURES[z.id](z)}</group> : null)}
      <FixtureHits zones={zones} />   {/* the lock-on: every portal and fixture is clickable */}

      {/* the people */}
      {npcs.map((n) => (n.walk ? <NpcWander key={n.id} n={n} /> : <NpcStand key={n.id} n={n} posRef={posRef} />))}

      {/* you, your companion, everyone else */}
      <Actor read={readMe} gender={gender} name={playerName} title={title} isLocal color="#f0cc60" />
      {companionId && <Companion read={readComp} companionId={companionId} radiantTier={radiantTier} />}
      {roster.map((r) => <RemoteActor key={r.sid} r={r} othersRef={othersRef} />)}

      {/* chat floaties — DOM, three seconds each, at most a few */}
      {floaties.map((f) => (
        <Html key={f.id} center position={[f.x + 0.5, 2.15, f.y + 0.5]} style={{ pointerEvents: "none" }} zIndexRange={[8, 0]}>
          <div style={{ background: "rgba(4,2,14,0.92)", border: `1px solid ${GM.amethyst}66`, borderRadius: 20, padding: "5px 12px", fontSize: 15, color: GM.vellum, fontFamily: GM.fontBody || GM.font, whiteSpace: "nowrap", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", textShadow: TEXT_SET }}>
            {f.text}
          </div>
        </Html>
      ))}

      <CameraRig posRef={posRef} />
      <FrameWatch />
    </>
  );
}
// ctl.player — the local player's position (tiles), for a body that looks at you (V'yen, 2026-09-23)
function NpcStand({ n, posRef }) {
  const ctl = useRef({ facing: n.facing, speed: 0, player: posRef }).current;
  return <group position={[n.x, n.y || 0, n.z]}><FolkBody id={n.spec} ctl={ctl} />
    <RidingHit fkey={"npc:" + n.id} label={n.id} h={folkHeight(n.spec)} w={n.spec === "vyen" ? 2.6 : n.spec === "willy" ? 1.2 : 0.8} /></group>;
}

// ═══════════════════════════════════════════════════════════════════
// A WALKING NPC — V'ael strolls the face of her own fountain.
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-22: "I definitely want her walking around soon" -> "yesss".
// NOTHING IN THE MODEL HAD TO CHANGE. vaelModel3D's useFrame already reads
// ctl.speed, eases the stride in and out and smooths its own yaw from
// ctl.facing; the only thing pinning her down was NpcStand hard-coding
// speed: 0. This moves a group and sets two fields.
//
// ⚠ ctl.speed IS A FLAG IN THIS PLAZA, NOT A VELOCITY. Every other call site
// sets it to exactly 1 or 0 — PlazaFolkStage's ?walk=1, the player, the
// remote actors — the sculpt reads `speed ? 1 : …` and the painted bodies
// multiply it into their walk phase. Passing metres-per-second here would
// mean nothing to the sculpt and would silently halve the painted fallback's
// cadence. Speed is the flag; `mps` is the movement.
//
// ⭐ mps IS MEASURED, NOT EYEBALLED. Her rig was posed through a full cycle
// in forward kinematics and the planted ankle tracked: her step is 0.188 m
// in model space, her cycle is 2*PI/7 s (the sculpt advances its phase at a
// FIXED 7 rad/s while walking, not proportionally to speed), and she renders
// at VAEL_H / MODEL_TOP = 0.852. Two steps per cycle at that scale is
// 0.36 units/s — the speed at which her feet stay planted. Change the number
// and she moonwalks; change her stride in vaelPose and re-measure.
//
// THE PATH is the open ground along the fountain's south face, and it is
// deliberately NOT a lap: plazaZones.js puts the herald's block at x 5-12 /
// z 6-9, so a circuit clips him at the north-west corner, and row z 13 is
// the only line clear across the whole fountain. One end of her walk is
// exactly where she used to stand.
//
// PERF LAW: nothing here sets React state from a frame.
function NpcWander({ n }) {
  const g = useRef();
  const ctl = useRef({ facing: n.facing, speed: 0 }).current;
  const st = useRef({ x: n.walk.x1, dir: -1, wait: 2.2 }).current;
  useFrame((_, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const w = n.walk;
    if (st.wait > 0) {
      st.wait -= dt;
      ctl.speed = 0;
      ctl.facing = { x: st.dir * 0.45, y: 1 };      // stopped, she looks out over the Plaza
    } else {
      st.x += w.mps * st.dir * dt;
      ctl.speed = 1;
      ctl.facing = { x: st.dir, y: 0 };             // walking, she faces the way she is going
      if (st.dir > 0 ? st.x >= w.x1 : st.x <= w.x0) {
        st.x = st.dir > 0 ? w.x1 : w.x0;
        st.dir = -st.dir;
        st.wait = w.pause[0] + Math.random() * (w.pause[1] - w.pause[0]);
      }
    }
    o.position.set(st.x, 0, w.z);
  });
  return <group ref={g}><FolkBody id={n.spec} ctl={ctl} /><RidingHit fkey={"npc:" + n.id} label={n.id} h={folkHeight(n.spec)} /></group>;
}
function RemoteActor({ r, othersRef }) {
  const read = useMemo(() => () => { const d = othersRef.current?.[r.sid]; return d ? { x: d.x || 0, y: d.y || 0, fx: d.facingX || 0, fy: d.facingY ?? 1 } : null; }, [r.sid, othersRef]);
  const readComp = useMemo(() => () => { const d = othersRef.current?.[r.sid]; if (!d) return null; return { x: (d.x || 0) - (d.facingX || 0), y: (d.y || 0) - (d.facingY ?? 1), fx: d.facingX || 0, fy: d.facingY ?? 1 }; }, [r.sid, othersRef]);
  return (
    <>
      <Actor read={read} gender={r.gender} name={r.name} title={r.title} color="#a78bfa" />
      {r.companionId && <Companion read={readComp} companionId={r.companionId} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE CAMERA BAR — same corner in every room. This screen renders inside
// Plaza.js, whose HUD stacks to z 55; the bar sits at 56, top-left,
// under the "← Title Screen" button, where nothing else lives.
// ═══════════════════════════════════════════════════════════════════
const camBtn = (on) => ({
  background: on ? `linear-gradient(135deg, ${GM.inkGold}2a, transparent)` : OBSIDIAN_SLAB,
  border: `1px solid ${on ? GM.inkGold : GM.inkGold + "44"}`,
  color: on ? GM.inkGoldBright : GM.vellumDim,
  fontFamily: GM.font, fontSize: 9.5, letterSpacing: 1.5, fontWeight: 700,
  padding: "4px 9px", borderRadius: 4, cursor: "pointer", textShadow: TEXT_SET,
});
export function CameraBar({ view, posed, force, mobile }) {
  return (
    <div style={{ position: "absolute", top: mobile ? 44 : 48, left: 14, zIndex: 56, display: "flex", gap: 6, alignItems: "center",
      background: "rgba(5,3,12,0.8)", border: `1px solid ${GM.pageBorder}`, borderRadius: 999, padding: "5px 8px", backdropFilter: "blur(6px)" }}>
      <span style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, fontFamily: GM.font, paddingLeft: 4 }}>CAMERA</span>
      {VIEW_ORDER.map((id) => (
        <button key={id} onClick={() => { chooseView(id); force((v) => v + 1); }} style={camBtn(view === id && !posed)}>{VIEWS[id].label}</button>
      ))}
      <button onClick={() => { resetCamera(); force((v) => v + 1); }} style={camBtn(false)}>DEFAULT</button>
      {getFocus() ? (
        <button onClick={() => { clearFocus(); force((v) => v + 1); }} title="Release the lock" style={camBtn(true)}>◎ LOCKED ✕</button>
      ) : posed && <span style={{ fontSize: 9, color: GM.inkGoldBright, fontFamily: GM.font, letterSpacing: 2, textShadow: textGlow(GM.inkGoldBright) }}>FREE</span>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE HOST — React.memo is the most important line in the file.
// ═══════════════════════════════════════════════════════════════════
function Plaza3DImpl({
  world, posRef, facingRef, gender, companionId, playerName, title, radiantTier,
  compOffsetRef, othersRef, rosterKey, floaties = [], mobile = false,
}) {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((v) => v + 1)), []);
  // the ROSTER, not the positions — rebuilt only when somebody arrives or leaves
  const roster = useMemo(() => Object.entries(othersRef.current || {})
    .filter(([, d]) => !!d).slice(0, 8)
    .map(([sid, d]) => ({ sid, gender: d.gender, companionId: d.companionId, name: d.name, title: getEquippedTitle(d.equipped) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rosterKey, othersRef]);
  const view = getView(); const posed = isPosed();
  return (
    <div style={{ position: "absolute", inset: 0, background: "#07040f" }}
      onPointerDownCapture={(e) => { DOWN.x = e.clientX; DOWN.y = e.clientY; }}>
      <Canvas
        dpr={[1, 1.5]} shadows
        camera={{ fov: 52, near: 0.2, far: 1400, position: [world.spawn.x + 0.5, 7, world.spawn.y + 8] }}
        gl={{ antialias: true, powerPreference: "high-performance", toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: G(LIGHT.exposure) }}
        onPointerMissed={(e) => { if (Math.hypot((e.clientX || 0) - DOWN.x, (e.clientY || 0) - DOWN.y) < 6) clearFocus(); }}   // a click on open world lets go of the lock (drags don't count)
        onCreated={(state) => {
          state.gl.setClearColor("#07040f");
          // dev only: the review loop reads draw calls and walks the scene
          // (`__plazaGL.info.render.calls`, `__plazaScene.traverse`)
          if (process.env.NODE_ENV === "development") { window.__plazaGL = state.gl; window.__plazaScene = state.scene; window.__plazaCam = state.camera; }
        }}
      >
        <Scene world={world} posRef={posRef} facingRef={facingRef} gender={gender} companionId={companionId}
          playerName={playerName} title={title} radiantTier={radiantTier} compOffsetRef={compOffsetRef}
          roster={roster} othersRef={othersRef} floaties={floaties} />
      </Canvas>
      <CameraBar view={view} posed={posed} force={force} mobile={mobile} />
    </div>
  );
}
export default React.memo(Plaza3DImpl);
